import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { formatOrderBookNumber } from "../../Common/OrderBookTooltip";
import { useTradingColors } from "../Spot/tradingColorConfig";
import "./OrderBookPage.css";

const parsePair = (pairStr) => {
  if (!pairStr) {
    const cur = localStorage.getItem("currency") || "BTC";
    const base = localStorage.getItem("base_currency") || "USDT";
    return { currency: cur.toUpperCase(), base: base.toUpperCase() };
  }
  if (pairStr.includes("_")) {
    const parts = pairStr.split("_");
    return { currency: parts[0].toUpperCase(), base: parts[1].toUpperCase() };
  }
  if (pairStr.includes("-")) {
    const parts = pairStr.split("-");
    return { currency: parts[0].toUpperCase(), base: parts[1].toUpperCase() };
  }
  if (pairStr.endsWith("USDT")) {
    return { currency: pairStr.replace(/USDT$/, "").toUpperCase(), base: "USDT" };
  }
  if (pairStr.endsWith("USD")) {
    return { currency: pairStr.replace(/USD$/, "").toUpperCase(), base: "USD" };
  }
  return { currency: pairStr.slice(0, 3).toUpperCase(), base: pairStr.slice(3).toUpperCase() || "USDT" };
};

const OrderBookPage = () => {
  const { pair } = useParams();
  const navigate = useNavigate();
  const { currency, base } = parsePair(pair);

  const { upColor = "#0ecb81", downColor = "#f6465d" } = useTradingColors();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");

  const [rawAsks, setRawAsks] = useState([]);
  const [rawBids, setRawBids] = useState([]);
  const [depthLimit, setDepthLimit] = useState(15);
  const [groupDecimals, setGroupDecimals] = useState(2);

  const stompClientRef = useRef(null);

  // Document title
  useEffect(() => {
    document.title = `Order Book - ${currency}/${base}`;
    return () => {
      document.title = "PayBito";
    };
  }, [currency, base]);

  // Sync theme changes
  useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem("theme") || "dark");
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // WebSocket connection & Polling fallback
  useEffect(() => {
    const fetchDepthSnapshot = async () => {
      try {
        const symbol = `${currency}${base}`;
        const token = localStorage.getItem("access_token");
        const headers = { "Content-Type": "application/json" };
        if (token) headers.authorization = `BEARER ${token}`;

        const res = await axios.get(
          `https://stream.paybito.com/SocketStream/api/depth?symbol=${symbol}&limit=150`,
          { headers }
        );

        if (res.data) {
          if (Array.isArray(res.data.ask)) {
            setRawAsks(res.data.ask);
          }
          if (Array.isArray(res.data.bid)) {
            setRawBids(res.data.bid);
          }
        }
      } catch (err) {
        // depth snapshot error handled gracefully
      }
    };

    fetchDepthSnapshot();
    const pollInterval = setInterval(fetchDepthSnapshot, 3000);

    let client = null;
    try {
      const ws = new SockJS("https://stream.paybito.com/SocketStream/ws");
      client = Stomp.over(ws);
      client.debug = () => {};

      client.connect(
        {},
        () => {
          stompClientRef.current = client;
          const topic =
            currency.toLowerCase() === "btc" && base.toLowerCase() === "usd"
              ? "/topic/stream/BTCUSD"
              : "/user/topic/stream";

          client.subscribe(topic, (msg) => {
            try {
              const data = JSON.parse(msg.body);
              if (data.e === "depthUpdate") {
                if (Array.isArray(data.ask)) {
                  setRawAsks((prev) => {
                    const map = new Map();
                    prev.forEach((item) => map.set(parseFloat(item.price), item));
                    data.ask.forEach((item) => {
                      const p = parseFloat(item.price);
                      const q = parseFloat(item.quantity !== undefined ? item.quantity : item.amount);
                      if (item.action === "DELETE" || item.action === "TRADEDELETE" || q <= 0) {
                        map.delete(p);
                      } else {
                        map.set(p, { price: item.price, amount: q });
                      }
                    });
                    return Array.from(map.values());
                  });
                }

                if (Array.isArray(data.bid)) {
                  setRawBids((prev) => {
                    const map = new Map();
                    prev.forEach((item) => map.set(parseFloat(item.price), item));
                    data.bid.forEach((item) => {
                      const p = parseFloat(item.price);
                      const q = parseFloat(item.quantity !== undefined ? item.quantity : item.amount);
                      if (item.action === "DELETE" || item.action === "TRADEDELETE" || q <= 0) {
                        map.delete(p);
                      } else {
                        map.set(p, { price: item.price, amount: q });
                      }
                    });
                    return Array.from(map.values());
                  });
                }
              }
            } catch (e) {
              // message parse error
            }
          });

          // Send subscription
          const reqNo = Math.floor(Math.random() * 1000) + 1;
          const subscribeReq = {
            method: "SUBSCRIBE",
            params: [
              `${currency}/${base}@ticker`,
              `${currency}${base}@depth`,
              `${currency}${base}@trade`,
            ],
            id: reqNo,
          };
          const sendEndpoint =
            currency.toLowerCase() === "btc" && base.toLowerCase() === "usd"
              ? "/app/sendRequest/BTCUSD"
              : "/app/sendRequest";
          client.send(sendEndpoint, {}, JSON.stringify(subscribeReq));
        },
        () => {
          // Socket error fallback handled by interval
        }
      );
    } catch (e) {
      // SockJS error handled by interval
    }

    return () => {
      clearInterval(pollInterval);
      if (client && client.connected) {
        try {
          client.disconnect();
        } catch (e) {}
      }
    };
  }, [currency, base]);

  // Group and Aggregate Orders
  const { buyOrders, sellOrders } = useMemo(() => {
    const factor = Math.pow(10, groupDecimals);

    // Group Bids: floor to decimal factor, sorted descending
    const groupedBidsMap = new Map();
    rawBids.forEach((item) => {
      const p = parseFloat(item.price) || 0;
      const a = parseFloat(item.amount !== undefined ? item.amount : item.quantity) || 0;
      if (p <= 0 || a <= 0) return;
      const groupedPrice = Math.floor(p * factor) / factor;
      groupedBidsMap.set(groupedPrice, (groupedBidsMap.get(groupedPrice) || 0) + a);
    });

    const sortedBids = Array.from(groupedBidsMap.entries())
      .map(([price, amount]) => ({ price, amount }))
      .sort((a, b) => b.price - a.price)
      .slice(0, depthLimit);

    let cumBidSum = 0;
    const computedBids = sortedBids.map((item) => {
      const total = item.price * item.amount;
      cumBidSum += total;
      return {
        price: item.price,
        amount: item.amount,
        total,
        sum: cumBidSum,
      };
    });

    const maxBidSum = cumBidSum || 1;
    computedBids.forEach((item) => {
      item.depthPercent = Math.min(100, Math.round((item.sum / maxBidSum) * 100));
    });

    // Group Asks: ceil to decimal factor, sorted ascending
    const groupedAsksMap = new Map();
    rawAsks.forEach((item) => {
      const p = parseFloat(item.price) || 0;
      const a = parseFloat(item.amount !== undefined ? item.amount : item.quantity) || 0;
      if (p <= 0 || a <= 0) return;
      const groupedPrice = Math.ceil(p * factor) / factor;
      groupedAsksMap.set(groupedPrice, (groupedAsksMap.get(groupedPrice) || 0) + a);
    });

    const sortedAsks = Array.from(groupedAsksMap.entries())
      .map(([price, amount]) => ({ price, amount }))
      .sort((a, b) => a.price - b.price)
      .slice(0, depthLimit);

    let cumAskSum = 0;
    const computedAsks = sortedAsks.map((item) => {
      const total = item.price * item.amount;
      cumAskSum += total;
      return {
        price: item.price,
        amount: item.amount,
        total,
        sum: cumAskSum,
      };
    });

    const maxAskSum = cumAskSum || 1;
    computedAsks.forEach((item) => {
      item.depthPercent = Math.min(100, Math.round((item.sum / maxAskSum) * 100));
    });

    return { buyOrders: computedBids, sellOrders: computedAsks };
  }, [rawBids, rawAsks, depthLimit, groupDecimals]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/spot");
    }
  };

  const isLight = theme === "light";
  const numColor = isLight ? "#1e2329" : "#eaecef";

  return (
    <div className={`orderbook-page-wrapper ${isLight ? "light" : "dark"}`}>
      {/* Top Back Navigation */}
      <div>
        <button className="orderbook-page-back-btn" onClick={handleBack}>
          <ArrowBackIcon sx={{ fontSize: "16px" }} />
          <span>Back</span>
        </button>
      </div>

      {/* Header bar: Title & Controls */}
      <div className="orderbook-page-header">
        <h1 className="orderbook-page-title">
          Order Book - {currency}/{base}
        </h1>

        <div className="orderbook-page-controls">
          {/* Depth Selector */}
          <div className="orderbook-control-group">
            <span className="orderbook-control-label">Depth</span>
            <select
              className="orderbook-select"
              value={depthLimit}
              onChange={(e) => setDepthLimit(Number(e.target.value))}
            >
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Grouping Selector */}
          <div className="orderbook-control-group">
            <span className="orderbook-control-label">Group</span>
            <select
              className="orderbook-select"
              value={groupDecimals}
              onChange={(e) => setGroupDecimals(Number(e.target.value))}
            >
              <option value={4}>4 decimals</option>
              <option value={2}>2 decimals</option>
              <option value={1}>1 decimal</option>
              <option value={0}>0 decimals</option>
            </select>
          </div>
        </div>
      </div>

      {/* Side-by-side Tables Grid */}
      <div className="orderbook-columns-grid">
        {/* Left Column: Buy Order */}
        <div className="orderbook-column">
          <h2 className="orderbook-panel-heading">Buy Order</h2>
          <table className="orderbook-page-table">
            <thead>
              <tr>
                <th className="col-price">Price ({base})</th>
                <th className="col-amount">Amount ({currency})</th>
                <th className="col-total">Total ({base})</th>
                <th className="col-sum">Sum ({base})</th>
              </tr>
            </thead>
            <tbody>
              {buyOrders.length > 0 ? (
                buyOrders.map((row, idx) => (
                  <tr
                    key={`buy-${row.price}-${idx}`}
                    className="orderbook-buy-row"
                    style={{ "--depth": `${row.depthPercent}%` }}
                  >
                    <td className="col-price" style={{ color: upColor }}>
                      {formatOrderBookNumber(row.price, groupDecimals)}
                    </td>
                    <td className="col-amount" style={{ color: numColor }}>
                      {formatOrderBookNumber(row.amount, 5)}
                    </td>
                    <td className="col-total" style={{ color: numColor }}>
                      {formatOrderBookNumber(row.total, 6)}
                    </td>
                    <td className="col-sum" style={{ color: numColor }}>
                      {formatOrderBookNumber(row.sum, 6)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#848e9c", padding: "20px" }}>
                    No Buy Orders
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Right Column: Sell Order */}
        <div className="orderbook-column">
          <h2 className="orderbook-panel-heading">Sell Order</h2>
          <table className="orderbook-page-table">
            <thead>
              <tr>
                <th className="col-price">Price ({base})</th>
                <th className="col-amount">Amount ({currency})</th>
                <th className="col-total">Total ({base})</th>
                <th className="col-sum">Sum ({base})</th>
              </tr>
            </thead>
            <tbody>
              {sellOrders.length > 0 ? (
                sellOrders.map((row, idx) => (
                  <tr
                    key={`sell-${row.price}-${idx}`}
                    className="orderbook-sell-row"
                    style={{ "--depth": `${row.depthPercent}%` }}
                  >
                    <td className="col-price" style={{ color: downColor }}>
                      {formatOrderBookNumber(row.price, groupDecimals)}
                    </td>
                    <td className="col-amount" style={{ color: numColor }}>
                      {formatOrderBookNumber(row.amount, 5)}
                    </td>
                    <td className="col-total" style={{ color: numColor }}>
                      {formatOrderBookNumber(row.total, 6)}
                    </td>
                    <td className="col-sum" style={{ color: numColor }}>
                      {formatOrderBookNumber(row.sum, 6)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#848e9c", padding: "20px" }}>
                    No Sell Orders
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderBookPage;
